import React, { useEffect, useState } from "react";
import { Users, ClipboardList, BarChart } from "lucide-react";
import { FilterState } from "./grade-distribution/GradeDistributionFilters";
import useUserStore from "../../../stores/user";
import supabase from "../../../utils/supabase";
import { useT } from "../../../stores/uiLang";
import { TD_T } from "../../../i18n/teacherDashboard";

export function Overview({ filters }: { filters: FilterState }) {
  const t = useT(TD_T);
  const { profile } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [totalStudents, setTotalStudents] = useState<number | null>(null);
  const [pendingAssignments, setPendingAssignments] = useState<number | null>(
    null
  );
  const [averagePerformance, setAveragePerformance] = useState<number | null>(
    null
  );

  const stats = [
    {
      title: t.totalStudents,
      value: totalStudents !== null ? totalStudents.toString() : "...",
      icon: <Users className="h-6 w-6 text-indigo-600" />,
      bgColor: "bg-indigo-100",
    },
    {
      title: t.pendingGrading,
      value:
        pendingAssignments !== null ? pendingAssignments.toString() : "...",
      icon: <ClipboardList className="h-6 w-6 text-orange-600" />,
      bgColor: "bg-orange-100",
    },
    {
      title: t.avgPerformance,
      value: averagePerformance !== null ? `${averagePerformance}%` : "...",
      icon: <BarChart className="h-6 w-6 text-green-600" />,
      bgColor: "bg-green-100",
    },
  ];

  async function getStudentsData(currentProfile = profile) {
    if (!currentProfile) return { studentIds: [], count: 0 };

    try {
      if (filters.selectedClass) {
        // If class is selected, get students from that specific class
        // First, get the class to verify it belongs to the teacher
        const { data: classData, error: classError } = await supabase
          .from("classes")
          .select("id")
          .eq("id", filters.selectedClass)
          .eq("user_id", currentProfile.id)
          .single();

        if (classError || !classData) {
          console.error("Error fetching class or class not found:", classError);
          return { studentIds: [], count: 0 };
        }

        // Get students enrolled in this specific class
        const { data, error } = await supabase
          .from("profiles")
          .select("id")
          .eq("role", "student")
          .eq("class", filters.selectedClass);

        if (error) {
          console.error("Error fetching students by class:", error);
          return { studentIds: [], count: 0 };
        }

        const studentIds = data?.map((student) => student.id) || [];
        return { studentIds, count: studentIds.length };
      }

      return { studentIds: [], count: 0 };
    } catch (error) {
      console.error("Error in getStudentsData:", error);
      return { studentIds: [], count: 0 };
    }
  }

  async function getPendingAssignmentsCount(studentIds: string[]) {
    if (studentIds.length === 0) return 0;

    try {
      const { data, error } = await supabase
        .from("student_assignments")
        .select("id", { count: "exact" })
        .in("student_id", studentIds)
        .eq("status", "in_progress");

      if (error) {
        console.error("Error fetching pending assignments:", error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error("Error in getPendingAssignmentsCount:", error);
      return 0;
    }
  }

  async function getAveragePerformance(studentIds: string[]) {
    if (studentIds.length === 0) return 0;

    try {
      const { data, error } = await supabase
        .from("student_assignments")
        .select("grade_percentage")
        .in("student_id", studentIds);

      if (error) {
        console.error("Error fetching assignment grades:", error);
        return 0;
      }

      if (!data || data.length === 0) return 0;

      // Filter out null/undefined grades and calculate average
      const validGrades = data
        .map((assignment) => assignment.grade_percentage)
        .filter((grade) => grade !== null && grade !== undefined);

      if (validGrades.length === 0) return 0;

      const average =
        validGrades.reduce((sum, grade) => sum + grade, 0) / validGrades.length;
      return Number(average.toFixed(0));
    } catch (error) {
      console.error("Error in getAveragePerformance:", error);
      return 0;
    }
  }

  useEffect(() => {
    // Only fetch data if profile is available
    if (!profile) return;

    const fetchData = async () => {
      setLoading(true);

      // Get students data (both count and IDs)
      const { studentIds, count: studentsCount } = await getStudentsData();
      setTotalStudents(studentsCount);

      // Get pending assignments count using student IDs
      const pendingCount = await getPendingAssignmentsCount(studentIds);
      setPendingAssignments(pendingCount);

      // Get average performance using student IDs
      const averagePerf = await getAveragePerformance(studentIds);
      setAveragePerformance(averagePerf);
      setLoading(false);
    };

    fetchData();
  }, [filters, profile?.id]);

  return (
    <section className="mb-10">
      <h2 className="text-lg font-medium text-gray-900 mb-5">{t.quickStats}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white overflow-hidden shadow rounded-lg cursor-pointer border border-gray-200 hover:shadow-lg transition-shadow duration-200 relative"
          >
            {loading && (
              <div className="absolute inset-0 bg-white rounded-lg">
                <div className="p-5 flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse"></div>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                    <div className="flex items-center mb-2">
                      <div className="h-5 bg-gray-200 rounded w-12 mr-2 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                    </div>
                    <div className="h-2 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="h-5 w-5 bg-gray-200 rounded ml-4 animate-pulse"></div>
                </div>
              </div>
            )}
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md p-3 ${stat.bgColor}`}>
                  {stat.icon}
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.title}
                    </dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">
                        {stat.value}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
